import { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import "./AdminPanel.css";

const LOGIN_URL = "https://func-xalq-chatbot.azurewebsites.net/api/mgmt/login";
const STATS_URL = "https://func-xalq-chatbot.azurewebsites.net/api/mgmt/stats";
const FEEDBACK_LIST_URL = "https://func-xalq-chatbot.azurewebsites.net/api/mgmt/feedbackList";
const TOKEN_KEY = "admin_token";

function getStoredToken() {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) return null;
  const dotIndex = token.indexOf(".");
  if (dotIndex === -1) return null;
  const expiresAt = parseInt(token.slice(0, dotIndex), 10);
  if (isNaN(expiresAt) || Date.now() > expiresAt) {
    localStorage.removeItem(TOKEN_KEY);
    return null;
  }
  return token;
}

function LoginScreen({ onLogin }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password.trim() || loading) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(LOGIN_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "Incorrect password");
      } else {
        localStorage.setItem(TOKEN_KEY, data.token);
        onLogin(data.token);
      }
    } catch {
      setError("Connection error. Please try again.");
    }
    setLoading(false);
  };

  return (
    <div className="admin-login-wrapper">
      <div className="admin-login-box">
        <div className="admin-login-logo">A</div>
        <h2 className="admin-login-title">Admin Panel</h2>
        <p className="admin-login-subtitle">Xalq Bank Azerbaijan</p>
        <form className="admin-login-form" onSubmit={handleSubmit}>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="admin-login-input"
            autoFocus
          />
          {error && <div className="admin-login-error">{error}</div>}
          <button
            type="submit"
            className="admin-login-btn"
            disabled={loading || !password.trim()}
          >
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub }) {
  return (
    <div className="admin-stat-card">
      <div className="admin-stat-value">{value}</div>
      <div className="admin-stat-label">{label}</div>
      {sub && <div className="admin-stat-sub">{sub}</div>}
    </div>
  );
}

function AnalyticsTab({ token, onLogout }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(STATS_URL, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        onLogout();
        return;
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to load stats");
      setStats(data);
    } catch (err) {
      setError("Failed to load analytics. Please try again.");
    }
    setLoading(false);
  };

  const topQuestion = stats?.topQuestions?.[0]?.text || "—";
  const topQuestionDisplay =
    topQuestion.length > 38 ? topQuestion.slice(0, 38) + "…" : topQuestion;

  return (
    <div className="admin-content">
      {loading ? (
        <div className="admin-loading">Loading analytics…</div>
      ) : error ? (
        <div className="admin-error">
          <span>{error}</span>
          <button className="admin-retry-btn" onClick={fetchStats}>
            Retry
          </button>
        </div>
      ) : stats ? (
        <>
          <div className="admin-stat-cards">
            <StatCard
              label="Total Conversations"
              value={stats.totalConversations.toLocaleString()}
            />
            <StatCard
              label="Total Messages"
              value={stats.totalMessages.toLocaleString()}
            />
            <StatCard
              label="Positive Feedback"
              value={`${stats.positiveFeedbackPercent}%`}
              sub={`${stats.positiveRatings} of ${stats.totalFeedback} rated`}
            />
            <StatCard label="Most Asked" value={topQuestionDisplay} />
          </div>

          <div className="admin-section">
            <h3 className="admin-section-title">
              Daily Conversations — Last 30 Days
            </h3>
            <div className="admin-chart-wrapper">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart
                  data={stats.dailyConversations}
                  margin={{ top: 4, right: 16, left: 0, bottom: 4 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="#E2E8F0"
                  />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fill: "#64748B" }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(d) => d.slice(5)}
                    interval={4}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#64748B" }}
                    tickLine={false}
                    axisLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: 8,
                      border: "1px solid #E2E8F0",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
                    }}
                    labelFormatter={(d) => `Date: ${d}`}
                  />
                  <Bar
                    dataKey="count"
                    name="Conversations"
                    fill="#2563B8"
                    radius={[3, 3, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="admin-section">
            <h3 className="admin-section-title">Top 10 Most Asked Questions</h3>
            {stats.topQuestions.length === 0 ? (
              <div className="admin-empty">No conversation data yet.</div>
            ) : (
              <div className="admin-questions-table-wrapper">
                <table className="admin-questions-table">
                  <thead>
                    <tr>
                      <th>Rank</th>
                      <th>Question</th>
                      <th>Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.topQuestions.map((q, i) => (
                      <tr key={i}>
                        <td className="admin-q-rank">{i + 1}</td>
                        <td>{q.text}</td>
                        <td className="admin-q-count">{q.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}

function FeedbackTab({ token, onLogout }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    fetchFeedback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchFeedback = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(FEEDBACK_LIST_URL, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        onLogout();
        return;
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to load feedback");
      setItems(data.items || []);
    } catch (err) {
      setError("Failed to load feedback. Please try again.");
    }
    setLoading(false);
  };

  const totalFeedback = items.length;
  const thumbsUp = items.filter((i) => i.rating === "up").length;
  const thumbsDown = items.filter((i) => i.rating === "down").length;
  const upPercent =
    totalFeedback > 0 ? Math.round((thumbsUp / totalFeedback) * 100) : 0;
  const downPercent =
    totalFeedback > 0 ? Math.round((thumbsDown / totalFeedback) * 100) : 0;

  const filtered =
    filter === "all" ? items : items.filter((i) => i.rating === filter);

  const formatDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="admin-content">
      {loading ? (
        <div className="admin-loading">Loading feedback…</div>
      ) : error ? (
        <div className="admin-error">
          <span>{error}</span>
          <button className="admin-retry-btn" onClick={fetchFeedback}>
            Retry
          </button>
        </div>
      ) : (
        <>
          <div className="feedback-summary">
            <div className="feedback-summary-card">
              <div className="feedback-summary-value">{totalFeedback}</div>
              <div className="feedback-summary-label">Total Ratings</div>
            </div>
            <div className="feedback-summary-card feedback-summary-card--up">
              <div className="feedback-summary-value">👍 {thumbsUp}</div>
              <div className="feedback-summary-label">{upPercent}% positive</div>
            </div>
            <div className="feedback-summary-card feedback-summary-card--down">
              <div className="feedback-summary-value">👎 {thumbsDown}</div>
              <div className="feedback-summary-label">{downPercent}% negative</div>
            </div>
          </div>

          <div className="admin-section">
            <div className="feedback-filter-row">
              <h3 className="admin-section-title admin-section-title--inline">
                Feedback Entries
              </h3>
              <div className="feedback-filter-toggles">
                <button
                  className={`feedback-filter-btn${filter === "all" ? " feedback-filter-btn--active" : ""}`}
                  onClick={() => setFilter("all")}
                >
                  All ({totalFeedback})
                </button>
                <button
                  className={`feedback-filter-btn${filter === "up" ? " feedback-filter-btn--active" : ""}`}
                  onClick={() => setFilter("up")}
                >
                  👍 Thumbs Up ({thumbsUp})
                </button>
                <button
                  className={`feedback-filter-btn${filter === "down" ? " feedback-filter-btn--active" : ""}`}
                  onClick={() => setFilter("down")}
                >
                  👎 Thumbs Down ({thumbsDown})
                </button>
              </div>
            </div>

            {filtered.length === 0 ? (
              <div className="admin-empty">No feedback entries yet.</div>
            ) : (
              <div className="feedback-table-wrapper">
                <table className="feedback-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Rating</th>
                      <th>Message</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((item, i) => (
                      <tr key={i}>
                        <td className="feedback-td-date">
                          {formatDate(item.timestamp)}
                        </td>
                        <td className="feedback-td-rating">
                          {item.rating === "up" ? "👍" : "👎"}
                        </td>
                        <td className="feedback-td-message">
                          {item.messageText.length > 80
                            ? item.messageText.slice(0, 80) + "…"
                            : item.messageText || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default function AdminPanel() {
  const [token, setToken] = useState(() => getStoredToken());
  const [activeTab, setActiveTab] = useState("analytics");

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  if (!token) {
    return <LoginScreen onLogin={setToken} />;
  }

  return (
    <div className="admin-panel">
      <aside className="admin-sidebar">
        <div className="admin-sidebar-brand">
          <div className="admin-sidebar-logo">A</div>
          <div>
            <div className="admin-sidebar-title">Admin Panel</div>
            <div className="admin-sidebar-sub">Xalq Bank Azerbaijan</div>
          </div>
        </div>
        <nav className="admin-sidebar-nav">
          <button
            className={`admin-nav-item${activeTab === "analytics" ? " admin-nav-item--active" : ""}`}
            onClick={() => setActiveTab("analytics")}
          >
            Analytics
          </button>
          <button
            className={`admin-nav-item${activeTab === "feedback" ? " admin-nav-item--active" : ""}`}
            onClick={() => setActiveTab("feedback")}
          >
            Feedback
          </button>
        </nav>
        <div className="admin-sidebar-footer">
          <button className="admin-logout-btn" onClick={handleLogout}>
            Sign Out
          </button>
        </div>
      </aside>

      <main className="admin-main">
        <div className="admin-main-inner">
          <h1 className="admin-page-title">
            {activeTab === "analytics" ? "Analytics" : "Feedback"}
          </h1>
          {activeTab === "analytics" ? (
            <AnalyticsTab token={token} onLogout={handleLogout} />
          ) : (
            <FeedbackTab token={token} onLogout={handleLogout} />
          )}
        </div>
      </main>
    </div>
  );
}
